/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : useViewport
 *   Feature      : 197-portal-light-theme-responsive
 *   Description  : Single source of the responsive viewport class
 *                  (desktop | tablet | mobile) for all responsive logic —
 *                  the adaptive navigation and the responsive rubric.
 *                  Thresholds come from the breakpoint tokens:
 *                    mobile  : width <  md (768)
 *                    tablet  : md <= width < lg (1024)
 *                    desktop : width >= lg (1024)
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 *   Created      : 2026-06-13
 * ============================================================================
 */
import { useEffect, useState } from 'react';

export type ViewportClass = 'desktop' | 'tablet' | 'mobile';

/** Breakpoint thresholds (px) — mirror the `--breakpoint-*` design tokens. */
export const VIEWPORT_BREAKPOINTS = { md: 768, lg: 1024 } as const;

/** Pure mapper: width (px) → viewport class. Exported for direct unit testing. */
export function classForWidth(width: number): ViewportClass {
  if (width < VIEWPORT_BREAKPOINTS.md) return 'mobile';
  if (width < VIEWPORT_BREAKPOINTS.lg) return 'tablet';
  return 'desktop';
}

function currentWidth(): number {
  // SSR / non-DOM safe: default to desktop width.
  return typeof window === 'undefined' ? VIEWPORT_BREAKPOINTS.lg : window.innerWidth;
}

export interface Viewport {
  class: ViewportClass;
  width: number;
}

/**
 * Reactive viewport hook. Re-renders on window resize / orientation change.
 * The single detection source for navigation presentation and the rubric.
 */
export function useViewport(): Viewport {
  const [width, setWidth] = useState<number>(() => currentWidth());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return { class: classForWidth(width), width };
}

export default useViewport;
